import React from "react";

/**
 * Shared styling for the KYC scorecard's flag sections - "Automated Screening Results" and
 * "Customer PEP Declaration (Onboarding)".
 *
 * A raised PEP/SIP flag has to be impossible to miss during compliance review, so a section with
 * any flag set is wrapped in the same yellow warning box as the "Identity Data Review Required"
 * banner. The palette is duplicated from that banner deliberately: both live in
 * KycIndividualModal.tsx as inline styles, and centralising them here is what stops the two
 * drifting apart the next time one is touched.
 */

/** Warning palette, matching the "Identity Data Review Required" banner exactly. */
export const FLAG_HIGHLIGHT_BACKGROUND = "#fff3cd";
export const FLAG_HIGHLIGHT_BORDER_COLOR = "#ffc107";

/**
 * Box around a flag section. Yellow only when that section has a flag raised - each section is
 * evaluated independently, so one can be highlighted while its neighbour is not.
 *
 * @param isHighlighted Whether this section has any flag set.
 * @returns Inline style for the section wrapper; empty when nothing is flagged.
 */
export const flagSectionStyle = (isHighlighted: boolean): React.CSSProperties =>
    isHighlighted
        ? {
              background: FLAG_HIGHLIGHT_BACKGROUND,
              border: `1px solid ${FLAG_HIGHLIGHT_BORDER_COLOR}`,
              borderRadius: "6px",
              padding: "12px 16px"
          }
        : {};

/**
 * Enlarges the flag checkboxes. They are native inputs rendering at ~13px by default, which is
 * what let a PEP/SIP flag blend into the dialog. Applied regardless of state.
 *
 * `pointerEvents: none` is what makes them non-interactive, rather than the `disabled` attribute:
 * browsers grey out a disabled checkbox no matter what accent colour is set, so a raised flag
 * rendered as a washed-out grey tick - the opposite of the point. Without `disabled` the box keeps
 * the platform's blue, matching the other checked boxes on the scorecard, and the inputs are still
 * `readOnly`, unfocusable (`tabIndex={-1}`) and announced as disabled (`aria-disabled`).
 */
/**
 * Blocks label activation on a flag checkbox. `pointerEvents: none` on the input alone is not
 * enough: clicking a <Label for="..."> forwards the click to the input, bypassing the input's own
 * pointer-events. Without this, a reviewer could tick a screening box by clicking its caption.
 */
export const flagLabelStyle: React.CSSProperties = { pointerEvents: "none" };

export const flagCheckboxStyle: React.CSSProperties = {
    width: "20px",
    height: "20px",
    pointerEvents: "none"
};
